import { useTranslation } from '@/src/i18n';
import { AppNotification, NotificationType } from '@/src/domain/models/Notification';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { PatternBackground } from '@/src/presentation/components';
import { ActivityIndicator, FlatList, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from './NotificationsScreen.styles';

export interface NotificationsScreenProps {
  notifications: AppNotification[];
  loading: boolean;
  onNotificationPress: (notification: AppNotification) => void;
  onMarkAllRead: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  onLoadMore: () => void;
  colors: {
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    border: string;
    divider: string;
    error: string;
    success: string;
  };
}

const getNotificationIcon = (type: NotificationType): { name: string; family: 'ionicons' | 'material' } => {
  switch (type) {
    case 'tournament_started':
      return { name: 'play-circle', family: 'ionicons' };
    case 'tournament_invitation':
      return { name: 'person-add', family: 'ionicons' };
    case 'friend_request':
      return { name: 'people', family: 'ionicons' };
    case 'challenge_received':
      return { name: 'sword-cross', family: 'material' };
    case 'match_result':
      return { name: 'trophy', family: 'ionicons' };
    case 'tournament_completed':
      return { name: 'checkmark-circle', family: 'ionicons' };
    case 'league_invitation':
      return { name: 'trophy-outline', family: 'material' };
    default:
      return { name: 'notifications', family: 'ionicons' };
  }
};

const getTimeAgo = (date: Date, t: (key: string, params?: Record<string, string | number>) => string): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('time.justNow');
  if (diffMins < 60) return t('time.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('time.hoursAgo', { count: diffHours });
  if (diffDays < 7) return t('time.daysAgo', { count: diffDays });
  return date.toLocaleDateString();
};

export const NotificationsScreen = ({
  notifications,
  loading,
  onNotificationPress,
  onMarkAllRead,
  onRefresh,
  refreshing,
  onLoadMore,
  colors,
}: NotificationsScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  const renderNotification = ({ item }: { item: AppNotification }) => {
    const icon = getNotificationIcon(item.type);

    return (
      <TouchableOpacity
        style={[
          styles.notificationItem,
          {
            backgroundColor: item.read ? colors.background : colors.surface,
            borderBottomColor: colors.divider,
          },
        ]}
        onPress={() => onNotificationPress(item)}
      >
        <View
          style={item.read ? styles.readIndicator : [styles.unreadIndicator, { backgroundColor: colors.primary }]}
        />
        <View style={[styles.iconContainer, { backgroundColor: colors.primary + '20' }]}>
          {icon.family === 'material' ? (
            <MaterialCommunityIcons name={icon.name as any} size={20} color={colors.primary} />
          ) : (
            <Ionicons name={icon.name as any} size={20} color={colors.primary} />
          )}
        </View>
        <View style={styles.notificationContent}>
          <Text style={[styles.notificationTitle, { color: colors.text }]}>{item.title}</Text>
          <Text style={[styles.notificationBody, { color: colors.textSecondary }]} numberOfLines={2}>
            {item.body}
          </Text>
          <Text style={[styles.notificationTime, { color: colors.textTertiary }]}>
            {getTimeAgo(item.createdAt, t)}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderEmpty = () => {
    if (loading) {
      return (
        <View style={styles.emptyContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      );
    }

    return (
      <View style={styles.emptyContainer}>
        <Ionicons name="notifications-off-outline" size={48} color={colors.textTertiary} />
        <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
          {t('notifications.empty')}
        </Text>
      </View>
    );
  };

  return (
    <PatternBackground>
      {notifications.length > 0 && (
        <TouchableOpacity style={styles.markAllButton} onPress={onMarkAllRead}>
          <Text style={[styles.markAllText, { color: colors.primary }]}>
            {t('notifications.markAllRead')}
          </Text>
        </TouchableOpacity>
      )}
      <FlatList
        data={notifications}
        renderItem={renderNotification}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={renderEmpty}
        onRefresh={onRefresh}
        refreshing={refreshing}
        onEndReached={onLoadMore}
        onEndReachedThreshold={0.5}
      />
    </PatternBackground>
  );
};
